import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@monksflow/monks-ui';
import { FormField } from '@/shared/ui/FormDialog';
import { SAME_AS_DISPLAY } from './sentinels';
import type { ListBinding } from './useListBinding';

/** Radix Select cannot hold an empty string, so "no list" needs a sentinel. */
// eslint-disable-next-line i18next/no-literal-string -- sentinel, not copy
const UNBOUND = '__unbound__';

export interface ListBindingFieldsProps {
  binding: ListBinding;
  /** Off for library templates, which always follow: only a schema column can freeze a version. */
  showFollow?: boolean;
  className?: string;
}

/** "Options from", "Codes from" and "Follow the list" for a dropdown field. */
export function ListBindingFields({
  binding,
  showFollow = true,
  className,
}: ListBindingFieldsProps) {
  const { t } = useTranslation('knowledge');
  const optionsId = useId();
  const codesId = useId();
  const { boundList, bindableLists, listId } = binding;

  const optionsHint =
    binding.unresolved && !binding.detailPending
      ? t('taxonomy.listBinding.listCannotBeBoundYet', { name: boundList?.name ?? '' })
      : boundList && !binding.follow && binding.pinVersionNumber !== undefined
        ? t('taxonomy.listBinding.pinnedToListVersion', {
            name: boundList.name,
            version: binding.pinVersionNumber,
          })
        : boundList
          ? t('taxonomy.listBinding.followsListWithOptions', {
              name: boundList.name,
              count: boundList.rowCount,
            })
          : binding.lists.length === 0 && !binding.listsPending
            ? t('taxonomy.listBinding.noSharedListsAvailableYet')
            : t('taxonomy.listBinding.bindSharedListKeepField');

  return (
    <div className={`flex flex-col gap-4 ${className ?? ''}`}>
      <FormField
        label={t('taxonomy.listBinding.optionsFrom')}
        htmlFor={optionsId}
        hint={optionsHint}
      >
        <Select
          value={listId || UNBOUND}
          onValueChange={(v) => binding.setListId(v === UNBOUND ? '' : v)}
          disabled={binding.listsPending}
        >
          <SelectTrigger id={optionsId} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNBOUND}>{t('taxonomy.listBinding.fieldsOwnValues')}</SelectItem>
            {binding.lists.map((l) =>
              bindableLists.includes(l) ? (
                <SelectItem key={l.id} value={l.id}>
                  {l.name} ({l.rowCount})
                </SelectItem>
              ) : (
                <SelectItem key={l.id} value={l.id} disabled>
                  {t('taxonomy.listBinding.listNeedsTwoColumns', { name: l.name })}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
      </FormField>

      {listId && binding.columns.length > 0 && (
        <FormField
          label={t('taxonomy.listBinding.codesFrom')}
          htmlFor={codesId}
          hint={
            binding.codeColumnId
              ? t('taxonomy.listBinding.codesFromColumnHint')
              : t('taxonomy.listBinding.codesFromSameHint')
          }
        >
          <Select
            value={binding.codeColumnId ?? SAME_AS_DISPLAY}
            onValueChange={(v) => binding.setCodeColumnId(v === SAME_AS_DISPLAY ? null : v)}
            disabled={binding.unresolved}
          >
            <SelectTrigger id={codesId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SAME_AS_DISPLAY}>
                {t('taxonomy.listBinding.sameAsDisplayValue')}
              </SelectItem>
              {binding.columns.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      )}

      {showFollow && listId && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <span className="text-sm font-medium">{t('taxonomy.linkListModal.followList')}</span>
            <span className="text-xs text-muted-foreground">
              {binding.canPin
                ? t('taxonomy.linkListModal.followOrPinExplainer', {
                    version: binding.pinVersionNumber ?? '—',
                  })
                : t('taxonomy.listBinding.nothingPublishedToPin')}
            </span>
          </div>
          <Switch
            checked={binding.follow}
            onCheckedChange={binding.setFollow}
            disabled={!binding.canPin || binding.unresolved}
            aria-label={t('taxonomy.linkListModal.followListHead')}
          />
        </div>
      )}
    </div>
  );
}
